import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useWebSocket } from './WebSocketContext';
import { useAuth } from './AuthContext';
import api, { callsAPI } from '../services/api';
import { tonesPlayer } from '../utils/tones';

const CallContext = createContext();

export const useCall = () => useContext(CallContext);

export const CallProvider = ({ children }) => {
  const { user } = useAuth();
  const { subscribeChat } = useWebSocket();
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);

  // Buffers for WebRTC signaling messages to prevent race conditions during call initiation
  const bufferedSignalsRef = useRef({});
  const signalHandlerRef = useRef(null);

  useEffect(() => {
    if (!user) {
      setActiveCall(null);
      setIncomingCall(null);
      bufferedSignalsRef.current = {};
      signalHandlerRef.current = null;
      return;
    }

    const checkActiveCall = async () => {
      try {
        const res = await api.get('/calls/active');
        if (res.data && res.data.id) {
          const callObj = res.data;
          if (callObj.callerId === user.id || callObj.status === 'connected') {
            setActiveCall(callObj);
          } else if (callObj.status === 'ringing') {
            setIncomingCall(callObj);
          }
        }
      } catch (err) {
        console.error("Failed to check active call:", err);
      }
    };

    checkActiveCall();
  }, [user]);

  const activeCallRef = useRef(null);
  const incomingCallRef = useRef(null);

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);

  useEffect(() => {
    if (!user) return;

    const unsub = subscribeChat((wsMsg) => {
      if (wsMsg.type === 'incoming_call') {
        // Prevent ringing if already in a call
        if (activeCallRef.current) return;
        setIncomingCall(wsMsg.call);
      } else if (wsMsg.type === 'call_accepted') {
        setActiveCall(prev => prev ? { ...prev, status: 'connected' } : prev);
      } else if (wsMsg.type === 'call_handled_elsewhere') {
        if (incomingCallRef.current && incomingCallRef.current.id === wsMsg.callId) {
          setIncomingCall(null);
        }
      } else if (wsMsg.type === 'call_ended' || wsMsg.type === 'call_rejected') {
        if (activeCallRef.current && activeCallRef.current.id === wsMsg.callId) {
          setActiveCall(null);
        }
        if (incomingCallRef.current && incomingCallRef.current.id === wsMsg.callId) {
          setIncomingCall(null);
        }
        // Clean up buffers for this call
        delete bufferedSignalsRef.current[wsMsg.callId];
      } else if (wsMsg.type === 'webrtc_signal') {
        const callId = wsMsg.callId;
        // Verify this is a signal for a call we care about to prevent crossover/stray messages
        const isCurrentCall = (activeCallRef.current && activeCallRef.current.id === callId) || 
                              (incomingCallRef.current && incomingCallRef.current.id === callId);
        if (isCurrentCall && signalHandlerRef.current) {
          signalHandlerRef.current(wsMsg.signal);
        } else {
          console.log(`[CallContext] Buffering/Queueing signal ${wsMsg.signal?.type} for call ${callId}`);
          if (!bufferedSignalsRef.current[callId]) {
            bufferedSignalsRef.current[callId] = [];
          }
          bufferedSignalsRef.current[callId].push(wsMsg.signal);
        }
      }
    });

    return unsub;
  }, [user, subscribeChat]);

  // Rings and dial tones management
  useEffect(() => {
    if (incomingCall) {
      tonesPlayer.playRingtone();
    } else if (activeCall && activeCall.status !== 'connected') {
      const isCaller = activeCall.callerId === user?.id;
      if (isCaller) {
        tonesPlayer.playDialTone();
      }
    } else {
      tonesPlayer.stop();
    }

    return () => {
      tonesPlayer.stop();
    };
  }, [incomingCall, activeCall, user]);

  const handleAcceptCall = async () => {
    if (!incomingCall) return;
    try {
      await callsAPI.answerCall(incomingCall.id, 'accept');
      setActiveCall(incomingCall);
      setIncomingCall(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleRejectCall = async () => {
    if (!incomingCall) return;
    try {
      await callsAPI.answerCall(incomingCall.id, 'reject');
    } catch (err) {
      console.error(err);
    }
    setIncomingCall(null);
  };

  const startCall = (callObj) => {
    setActiveCall(callObj);
  };

  const endCall = () => {
    setActiveCall(null);
    bufferedSignalsRef.current = {};
    signalHandlerRef.current = null;
  };

  const registerSignalHandler = useCallback((callId, handler) => {
    signalHandlerRef.current = handler;
    // Immediately drain any signals buffered for this call
    if (bufferedSignalsRef.current[callId]) {
      const signals = bufferedSignalsRef.current[callId];
      delete bufferedSignalsRef.current[callId];
      console.log(`[CallContext] Draining ${signals.length} buffered signals for call ${callId}`);
      signals.forEach(sig => {
        try {
          handler(sig);
        } catch (err) {
          console.error("[CallContext] Error draining signal:", err);
        }
      });
    }
  }, []);

  const unregisterSignalHandler = useCallback(() => {
    signalHandlerRef.current = null;
  }, []);

  return (
    <CallContext.Provider value={{
      activeCall,
      incomingCall,
      handleAcceptCall,
      handleRejectCall,
      startCall,
      endCall,
      registerSignalHandler,
      unregisterSignalHandler
    }}>
      {children}
    </CallContext.Provider>
  );
};
